import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import './VoiceAgent.css';

const JOURS = [
  { id: 'lun', label: 'Lundi' },
  { id: 'mar', label: 'Mardi' },
  { id: 'mer', label: 'Mercredi' },
  { id: 'jeu', label: 'Jeudi' },
  { id: 'ven', label: 'Vendredi' },
  { id: 'sam', label: 'Samedi' },
  { id: 'dim', label: 'Dimanche' },
];

// Horaires non renseignés : aucun jour coché. L'agent ne donne alors aucun
// horaire au client plutôt que des heures par défaut qui seraient fausses.
const DEFAULT_HORAIRES = Object.fromEntries(
  JOURS.map(j => [j.id, { actif: false, debut: '08:00', fin: '17:00' }])
);

const ACTION_LABELS = {
  rdv_booked:          { label: 'RDV pris',        cls: 'status-success' },
  callback_requested:  { label: 'Rappel demandé',  cls: 'status-info' },
  no_action:           { label: 'Aucune action',   cls: 'status-warning' },
  transferred:         { label: 'Transféré',       cls: 'status-info' },
};

function formatDuration(secs) {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function ToggleSwitch({ checked, onChange, disabled }) {
  return (
    <button
      className={`va-toggle${checked ? ' on' : ''}`}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      title={checked ? 'Désactiver' : 'Activer'}
    >
      <span className="va-toggle-thumb" />
    </button>
  );
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button className="va-copy-btn" onClick={copy} title="Copier">
      {copied ? '✅' : '📋'}
    </button>
  );
}

// ── Fiche d'une concession pour l'agent vocal ───────────────────────────────
// Un seul agent sert toutes les concessions : il reconnaît la concession au
// numéro du standard appelé et lit cette fiche au décroché (variable
// {{infos_concession}}). Rien à modifier dans le prompt ni dans ElevenLabs.
function FicheConcessionEditor({ concession, onSave }) {
  const [open, setOpen] = useState(false);
  const [numero, setNumero] = useState(concession.voice_agent_numero || '');
  const [nomPrononce, setNomPrononce] = useState(concession.voice_agent_nom_prononce || '');
  const [infos, setInfos] = useState(concession.voice_agent_infos || '');
  const [horaires, setHoraires] = useState(
    concession.voice_agent_horaires || { ...DEFAULT_HORAIRES }
  );
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState(null);

  function setJour(id, field, value) {
    setHoraires(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value }
    }));
  }

  async function handleSave() {
    setSaving(true);
    setErreur(null);
    try {
      await onSave(concession.id, { numero, nom_prononce: nomPrononce, infos }, horaires);
      setOpen(false);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }

  const joursActifs = JOURS.filter(j => horaires[j.id]?.actif).map(j => j.label.slice(0, 3)).join(' · ');

  return (
    <div className="va-horaires-block">
      <div className="va-horaires-header" onClick={() => setOpen(o => !o)}>
        <span className="va-horaires-nom">{concession.nom}</span>
        <span className="va-horaires-summary">
          {concession.voice_agent_numero || 'Aucun numéro'} · {joursActifs || 'Horaires non renseignés'}
        </span>
        <span className="va-horaires-chevron">{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div className="va-horaires-body">
          <div className="va-agent-field">
            <label>
              Numéro du standard{' '}
              <span className="va-param-hint">(celui importé dans ElevenLabs — c'est lui qui fait reconnaître la concession)</span>
            </label>
            <input
              className="va-param-input"
              type="tel"
              value={numero}
              onChange={e => setNumero(e.target.value)}
              placeholder="+590 590 00 00 00"
            />
          </div>

          <div className="va-agent-field">
            <label>
              Nom prononcé par l'agent{' '}
              <span className="va-param-hint">(vide : nom de la concession dans le CRM)</span>
            </label>
            <input
              className="va-param-input"
              type="text"
              value={nomPrononce}
              onChange={e => setNomPrononce(e.target.value)}
              placeholder={concession.nom}
            />
          </div>

          <div className="va-agent-field">
            <label>
              Informations pour le client{' '}
              <span className="va-param-hint">(marques entretenues, adresse, pause déjeuner… quelques lignes, l'agent ne donne rien d'autre ; jamais de nom de collaborateur)</span>
            </label>
            <textarea
              className="va-agent-textarea"
              rows={4}
              value={infos}
              onChange={e => setInfos(e.target.value)}
              placeholder="Marques entretenues : …"
            />
          </div>

          <div className="va-agent-field">
            <label>
              Horaires d'ouverture{' '}
              <span className="va-param-hint">(annoncés au client qui les demande ; les créneaux de RDV suivent le planning atelier)</span>
            </label>
          </div>
          <table className="va-horaires-table">
            <thead>
              <tr>
                <th>Jour</th>
                <th style={{ textAlign: 'center' }}>Ouvert</th>
                <th>Début</th>
                <th>Fin</th>
              </tr>
            </thead>
            <tbody>
              {JOURS.map(j => {
                const h = horaires[j.id] || { actif: false, debut: '08:00', fin: '17:00' };
                return (
                  <tr key={j.id} style={{ opacity: h.actif ? 1 : 0.45 }}>
                    <td style={{ fontWeight: 600 }}>{j.label}</td>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!h.actif}
                        onChange={e => setJour(j.id, 'actif', e.target.checked)}
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        className="va-time-input"
                        value={h.debut}
                        disabled={!h.actif}
                        onChange={e => setJour(j.id, 'debut', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        className="va-time-input"
                        value={h.fin}
                        disabled={!h.actif}
                        onChange={e => setJour(j.id, 'fin', e.target.value)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
            {erreur && <span className="va-sync-error">⚠️ {erreur}</span>}
            <button className="btn-secondary" onClick={() => setOpen(false)}>Annuler</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Bloc prompt / voix d'un agent (une concession = un agent ElevenLabs) ────
// Le CRM est la source de vérité : « Enregistrer » écrit en base PUIS pousse
// vers ElevenLabs (création à la 1re sauvegarde, mise à jour ensuite).
function AgentPromptEditor({ agent, voices, onSave }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(agent.prompt || '');
  const [firstMessage, setFirstMessage] = useState(agent.first_message || '');
  const [voiceId, setVoiceId] = useState(agent.voice_id || '');
  const [stability, setStability] = useState(
    agent.voice_stability !== null && agent.voice_stability !== undefined ? Number(agent.voice_stability) : 0.5
  );
  const [similarityBoost, setSimilarityBoost] = useState(
    agent.voice_similarity_boost !== null && agent.voice_similarity_boost !== undefined
      ? Number(agent.voice_similarity_boost) : 0.75
  );
  const [saving, setSaving] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  const hasAgent = !!agent.elevenlabs_agent_id;

  async function handleSave() {
    setSaving(true);
    setSyncStatus(null);
    try {
      const result = await onSave(agent.concession_id, {
        prompt, first_message: firstMessage, voice_id: voiceId || null,
        voice_stability: stability, voice_similarity_boost: similarityBoost,
      });
      setSyncStatus(result?.elevenlabs_sync || null);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="va-horaires-block">
      <div className="va-horaires-header" onClick={() => setOpen(o => !o)}>
        <span className="va-horaires-nom">{agent.nom}</span>
        <span className="va-horaires-summary">
          {hasAgent
            ? <span className="va-agent-status ok">✅ Agent créé</span>
            : <span className="va-agent-status pending">○ Pas encore créé</span>}
          {prompt.length > 0 && <span> · {prompt.length} caractères de prompt</span>}
        </span>
        <span className="va-horaires-chevron">{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div className="va-horaires-body">
          <div className="va-agent-field">
            <label>Prompt <span className="va-param-hint">(personnalité, mission, contraintes de l'agent — s'enrichit au fil du temps)</span></label>
            <textarea
              className="va-agent-textarea"
              rows={10}
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder={`Tu es l'assistant vocal du garage ${agent.nom}. Ta mission est de prendre un rendez-vous atelier ou de noter une demande de rappel si le client le souhaite...`}
            />
          </div>

          <div className="va-agent-field">
            <label>Message d'accueil</label>
            <input
              className="va-param-input"
              type="text"
              value={firstMessage}
              onChange={e => setFirstMessage(e.target.value)}
              placeholder="Bonjour, vous êtes en relation avec l'assistant virtuel du garage..."
            />
          </div>

          <div className="va-agent-field">
            <label>Voix</label>
            <select className="page-select" value={voiceId} onChange={e => setVoiceId(e.target.value)}>
              <option value="">Voix par défaut de l'agent</option>
              {voices.map(v => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}
            </select>
          </div>

          <div className="va-agent-field">
            <label>
              Stabilité de la voix{' '}
              <span className="va-param-hint">
                (basse = plus expressive mais peut varier en cours d'appel, haute = plus constante mais plus monocorde)
              </span>
            </label>
            <div className="va-agent-slider-row">
              <input
                type="range" min="0" max="1" step="0.05"
                value={stability}
                onChange={e => setStability(Number(e.target.value))}
              />
              <span className="va-agent-slider-value">{stability.toFixed(2)}</span>
            </div>
          </div>

          <div className="va-agent-field">
            <label>
              Fidélité à la voix d'origine{' '}
              <span className="va-param-hint">(similarity boost — haute = plus proche du timbre choisi)</span>
            </label>
            <div className="va-agent-slider-row">
              <input
                type="range" min="0" max="1" step="0.05"
                value={similarityBoost}
                onChange={e => setSimilarityBoost(Number(e.target.value))}
              />
              <span className="va-agent-slider-value">{similarityBoost.toFixed(2)}</span>
            </div>
          </div>

          {hasAgent && (
            <div className="va-agent-id-row">
              <span className="va-param-hint">ID agent ElevenLabs :</span>
              <code className="va-endpoint-url va-agent-id-code">{agent.elevenlabs_agent_id}</code>
              <CopyButton value={agent.elevenlabs_agent_id} />
            </div>
          )}

          <div className="va-param-save-row">
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            {syncStatus && (syncStatus.success
              ? <span className="va-save-ok">✅ Synchronisé avec ElevenLabs</span>
              : <span className="va-sync-error">⚠️ Enregistré en local — échec ElevenLabs : {syncStatus.error}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Bloc annuaire d'une concession (collaborateurs joignables par transfert) ─
// Sert à l'outil ElevenLabs rechercher_annuaire : quand un client demande à
// parler à quelqu'un nommément, l'agent recherche ici puis transfère l'appel
// (outil natif transfer_to_number) si un téléphone direct est trouvé.
function AnnuaireEditor({ concessionId, concessionNom, entries, onAdd, onUpdate, onDelete }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(entries);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setRows(entries); }, [entries]);

  function setRow(idx, field, value) {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  }

  function addRow() {
    setRows(prev => [...prev, { id: null, nom: '', poste: '', telephone: '', mots_cles: '' }]);
  }

  async function handleDelete(row, idx) {
    if (!row.id) { setRows(prev => prev.filter((_, i) => i !== idx)); return; }
    if (!window.confirm(`Retirer ${row.nom || 'ce collaborateur'} de l'annuaire ?`)) return;
    await onDelete(row.id);
  }

  async function handleSave() {
    setSaving(true);
    try {
      for (const row of rows) {
        if (!row.nom || !row.telephone) continue;
        if (row.id) {
          const original = entries.find(e => e.id === row.id);
          const changed = original && (
            original.nom !== row.nom || original.poste !== row.poste ||
            original.telephone !== row.telephone || original.mots_cles !== row.mots_cles
          );
          if (changed) await onUpdate(row.id, row);
        } else {
          await onAdd(concessionId, row);
        }
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="va-horaires-block">
      <div className="va-horaires-header" onClick={() => setOpen(o => !o)}>
        <span className="va-horaires-nom">{concessionNom}</span>
        <span className="va-horaires-summary">
          {entries.length} collaborateur{entries.length !== 1 ? 's' : ''} dans l'annuaire
        </span>
        <span className="va-horaires-chevron">{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div className="va-horaires-body">
          <table className="va-horaires-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Service / poste</th>
                <th>Téléphone direct</th>
                <th>Mots-clés <span className="va-param-hint">(alias reconnus, séparés par des virgules)</span></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.id ?? `new-${idx}`}>
                  <td><input className="va-param-input" value={row.nom} onChange={e => setRow(idx, 'nom', e.target.value)} placeholder="Jean Dupont" /></td>
                  <td><input className="va-param-input" value={row.poste || ''} onChange={e => setRow(idx, 'poste', e.target.value)} placeholder="Responsable atelier" /></td>
                  <td><input className="va-param-input" value={row.telephone} onChange={e => setRow(idx, 'telephone', e.target.value)} placeholder="0596xxxxxx" /></td>
                  <td><input className="va-param-input" value={row.mots_cles || ''} onChange={e => setRow(idx, 'mots_cles', e.target.value)} placeholder="atelier, SAV" /></td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon delete" title="Retirer" onClick={() => handleDelete(row, idx)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'space-between' }}>
            <button className="btn-secondary" onClick={addRow}>+ Ajouter un collaborateur</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────
function VoiceAgent({ user, setHeaderAction }) {
  const [activeTab, setActiveTab] = useState('config');

  // Config tab
  const [concessions, setConcessions] = useState([]);
  const [configLoading, setConfigLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  // Calls tab
  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsTotal, setCallsTotal] = useState(0);
  // Concession de l'historique : suit le choix du menu (2026-10-26) — la page est remontée à chaque changement.
  const [filterConcession] = useState(() => (user?.concession_id ? String(user.concession_id) : ''));
  const [filterAction, setFilterAction] = useState('');
  const [transcript, setTranscript] = useState(null);

  // Paramètres tab
  const [globalConfig, setGlobalConfig] = useState({
    ringover_api_key: '',
    ringover_ivr_id: '',
  });
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [webhookSecretConfigured, setWebhookSecretConfigured] = useState(false);
  const [publicUrl, setPublicUrl] = useState('');
  const [configSaving, setConfigSaving] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const [paramLoading, setParamLoading] = useState(false);
  const saveTimer = useRef(null);

  // Agents IA par concession (prompt/voix)
  const [agents, setAgents] = useState([]);
  const [elevenlabsConfigured, setElevenlabsConfigured] = useState(false);
  const [voices, setVoices] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  // Annuaire par concession (transfert d'appel)
  const [annuaire, setAnnuaire] = useState([]);
  const [annuaireLoading, setAnnuaireLoading] = useState(false);

  useEffect(() => {
    if (setHeaderAction) setHeaderAction(null);
  }, [setHeaderAction]);

  // Charger concessions
  const loadConcessions = useCallback(async () => {
    setConfigLoading(true);
    try {
      const r = await api.get('/voice-agent/admin/concessions');
      setConcessions(r.data.concessions || []);
    } catch (e) { console.error(e); }
    finally { setConfigLoading(false); }
  }, []);

  // Charger appels
  const loadCalls = useCallback(async () => {
    setCallsLoading(true);
    try {
      const params = {};
      if (filterConcession) params.concession_id = filterConcession;
      if (filterAction) params.action_taken = filterAction;
      const r = await api.get('/voice-agent/admin/calls', { params });
      setCalls(r.data.calls || []);
      setCallsTotal(r.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setCallsLoading(false); }
  }, [filterConcession, filterAction]);

  // Charger config globale
  const loadConfig = useCallback(async () => {
    setParamLoading(true);
    try {
      const r = await api.get('/voice-agent/admin/config');
      setGlobalConfig(prev => ({ ...prev, ...(r.data.config || {}) }));
      setApiKeyConfigured(r.data.api_key_configured || false);
      setWebhookSecretConfigured(r.data.webhook_secret_configured || false);
      setPublicUrl(r.data.public_url || window.location.origin);
    } catch (e) { console.error(e); }
    finally { setParamLoading(false); }
  }, []);

  // Charger agents IA par concession + voix disponibles
  const loadAgents = useCallback(async () => {
    setAgentsLoading(true);
    try {
      const r = await api.get('/voice-agent/admin/agents');
      setAgents(r.data.agents || []);
      setElevenlabsConfigured(r.data.elevenlabs_configured || false);
    } catch (e) { console.error(e); }
    finally { setAgentsLoading(false); }
  }, []);

  const loadVoices = useCallback(async () => {
    try {
      const r = await api.get('/voice-agent/admin/voices');
      setVoices(r.data.voices || []);
    } catch (e) { console.error(e); /* optionnel : la voix par défaut reste utilisable */ }
  }, []);

  // Charger annuaire (tous les collaborateurs, toutes concessions)
  const loadAnnuaire = useCallback(async () => {
    setAnnuaireLoading(true);
    try {
      const r = await api.get('/voice-agent/admin/annuaire');
      setAnnuaire(r.data.annuaire || []);
    } catch (e) { console.error(e); }
    finally { setAnnuaireLoading(false); }
  }, []);

  useEffect(() => { loadConcessions(); }, [loadConcessions]);
  useEffect(() => {
    if (activeTab === 'calls') loadCalls();
  }, [activeTab, loadCalls]);
  useEffect(() => {
    if (activeTab === 'params') { loadConfig(); loadAgents(); loadVoices(); loadAnnuaire(); }
  }, [activeTab, loadConfig, loadAgents, loadVoices, loadAnnuaire]);

  async function handleSaveAgent(concessionId, payload) {
    const r = await api.put(`/voice-agent/admin/agents/${concessionId}`, payload);
    setAgents(prev => prev.map(a =>
      a.concession_id === concessionId ? { ...a, ...r.data.agent } : a
    ));
    return r.data;
  }

  async function handleToggle(id, val) {
    setToggling(id);
    try {
      const r = await api.put(`/voice-agent/admin/concessions/${id}`, { voice_agent_enabled: val });
      setConcessions(prev => prev.map(c =>
        c.id === id ? { ...c, voice_agent_enabled: r.data.voice_agent_enabled } : c
      ));
    } catch (e) { console.error(e); }
    finally { setToggling(null); }
  }

  async function handleSaveFiche(concessionId, fiche, horaires) {
    const f = await api.put(`/voice-agent/admin/concessions/${concessionId}/fiche`, fiche);
    const h = await api.put(`/voice-agent/admin/concessions/${concessionId}/horaires`, { horaires });
    setConcessions(prev => prev.map(c =>
      c.id === concessionId ? { ...c, ...f.data, voice_agent_horaires: h.data.voice_agent_horaires } : c
    ));
  }

  async function handleAddAnnuaire(concessionId, entry) {
    const r = await api.post('/voice-agent/admin/annuaire', { concession_id: concessionId, ...entry });
    setAnnuaire(prev => [...prev, r.data]);
  }

  async function handleUpdateAnnuaire(id, entry) {
    const r = await api.put(`/voice-agent/admin/annuaire/${id}`, entry);
    setAnnuaire(prev => prev.map(a => a.id === id ? r.data : a));
  }

  async function handleDeleteAnnuaire(id) {
    await api.delete(`/voice-agent/admin/annuaire/${id}`);
    setAnnuaire(prev => prev.filter(a => a.id !== id));
  }

  function handleConfigChange(field, value) {
    setGlobalConfig(prev => ({ ...prev, [field]: value }));
    setConfigSaved(false);
  }

  async function handleSaveConfig() {
    setConfigSaving(true);
    try {
      await api.put('/voice-agent/admin/config', globalConfig);
      setConfigSaved(true);
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => setConfigSaved(false), 3000);
    } catch (e) { console.error(e); }
    finally { setConfigSaving(false); }
  }

  const enabledCount = concessions.filter(c => c.voice_agent_enabled).length;
  const prodBase = window.location.origin;

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={activeTab === 'config' ? 'active' : ''} onClick={() => setActiveTab('config')}>
          📋 Configuration
        </button>
        <button className={activeTab === 'calls' ? 'active' : ''} onClick={() => setActiveTab('calls')}>
          📞 Historique des appels
        </button>
        <button className={activeTab === 'params' ? 'active' : ''} onClick={() => setActiveTab('params')}>
          ⚙️ Paramètres
        </button>
      </div>

      {/* ── Tab : Configuration ───────────────────────────── */}
      {activeTab === 'config' && (
        <>
          <div className="va-stats-row">
            <div className="va-stat-card">
              <div className="va-stat-value">{concessions.length}</div>
              <div className="va-stat-label">Concessions actives</div>
            </div>
            <div className="va-stat-card accent">
              <div className="va-stat-value">{enabledCount}</div>
              <div className="va-stat-label">Agent vocal activé</div>
            </div>
            <div className="va-stat-card">
              <div className="va-stat-value">{concessions.length - enabledCount}</div>
              <div className="va-stat-label">Agent vocal inactif</div>
            </div>
          </div>

          <div className="va-info-banner">
            <span className="va-info-icon">🎙️</span>
            <div>
              <strong>Agent vocal IA (ElevenLabs × Ringover)</strong> — En cas de non-réponse,
              Ringover reroute l'appel vers l'agent IA qui prend un RDV atelier ou note une
              demande de rappel, puis pousse les données dans le CRM.
            </div>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Concession</th>
                  <th>Slug</th>
                  <th style={{ textAlign: 'center' }}>Agent vocal</th>
                </tr>
              </thead>
              <tbody>
                {configLoading ? (
                  <tr><td colSpan={3} className="va-empty">Chargement…</td></tr>
                ) : concessions.length === 0 ? (
                  <tr><td colSpan={3} className="va-empty">Aucune concession</td></tr>
                ) : concessions.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600 }}>{c.nom}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--text-secondary)' }}>{c.slug}</td>
                    <td style={{ textAlign: 'center' }}>
                      <ToggleSwitch
                        checked={!!c.voice_agent_enabled}
                        onChange={val => handleToggle(c.id, val)}
                        disabled={toggling === c.id}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Tab : Historique des appels ───────────────────── */}
      {activeTab === 'calls' && (
        <>
          <div className="page-toolbar">
            {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
            <select
              className={`page-select${filterAction ? ' active-filter' : ''}`}
              value={filterAction}
              onChange={e => setFilterAction(e.target.value)}
            >
              <option value="">Toutes les actions</option>
              <option value="rdv_booked">RDV pris</option>
              <option value="callback_requested">Rappel demandé</option>
              <option value="no_action">Aucune action</option>
              <option value="transferred">Transféré</option>
            </select>
            <button className="page-select" onClick={loadCalls} style={{ cursor: 'pointer' }}>
              🔄 Actualiser
            </button>
            <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)', alignSelf: 'center' }}>
              {callsTotal} appel{callsTotal !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Concession</th>
                  <th>Durée</th>
                  <th>Action</th>
                  <th>Conversation ID</th>
                  <th>Transcript</th>
                </tr>
              </thead>
              <tbody>
                {callsLoading ? (
                  <tr><td colSpan={6} className="va-empty">Chargement…</td></tr>
                ) : calls.length === 0 ? (
                  <tr><td colSpan={6} className="va-empty">Aucun appel enregistré</td></tr>
                ) : calls.map(call => {
                  const actionInfo = ACTION_LABELS[call.action_taken] || { label: call.action_taken || '—', cls: '' };
                  return (
                    <tr key={call.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(call.created_at)}</td>
                      <td>{call.concession_nom || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{formatDuration(call.duration_seconds)}</td>
                      <td>
                        {call.action_taken
                          ? <span className={`va-badge ${actionInfo.cls}`}>{actionInfo.label}</span>
                          : '—'}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>
                        {call.elevenlabs_conversation_id
                          ? call.elevenlabs_conversation_id.slice(0, 16) + '…'
                          : '—'}
                      </td>
                      <td>
                        {call.transcript
                          ? <button className="btn-icon" title="Voir le transcript" onClick={() => setTranscript(call)}>👁️</button>
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Tab : Paramètres ─────────────────────────────── */}
      {activeTab === 'params' && (
        <>
          {paramLoading ? (
            <div className="va-empty">Chargement…</div>
          ) : (
            <>
              {/* ── Section 1 : Clés API ── */}
              <div className="va-param-section">
                <div className="va-param-section-title">🔑 Clés API</div>

                <div className="va-param-grid">
                  {/* VOICE_AGENT_API_KEY — depuis .env, non éditable */}
                  <div className="va-param-field full">
                    <label>Clé partagée CRM ↔ ElevenLabs <span className="va-param-hint">(variable <code>VOICE_AGENT_API_KEY</code> dans <code>.env</code>)</span></label>
                    <div className="va-param-row">
                      <div className={`va-key-status${apiKeyConfigured ? ' ok' : ' missing'}`}>
                        {apiKeyConfigured ? '✅ Configurée dans .env' : '❌ Non configurée — ajouter VOICE_AGENT_API_KEY dans .env du serveur'}
                      </div>
                    </div>
                    <p className="va-param-desc">
                      Header <code>X-Api-Key</code> envoyé par ElevenLabs sur chaque appel outil et webhook.
                      Générer une chaîne aléatoire ≥ 32 caractères, la placer dans <code>.env</code> backend et dans la configuration ElevenLabs.
                    </p>
                  </div>

                  {/* ELEVENLABS_API_KEY — depuis .env, non éditable */}
                  <div className="va-param-field full">
                    <label>Clé de compte ElevenLabs <span className="va-param-hint">(variable <code>ELEVENLABS_API_KEY</code> dans <code>.env</code> — pilote les agents par concession ci-dessous)</span></label>
                    <div className="va-param-row">
                      <div className={`va-key-status${elevenlabsConfigured ? ' ok' : ' missing'}`}>
                        {elevenlabsConfigured ? '✅ Configurée dans .env' : '❌ Non configurée — ajouter ELEVENLABS_API_KEY dans .env du serveur'}
                      </div>
                    </div>
                  </div>

                  {/* ELEVENLABS_WEBHOOK_SECRET — depuis .env, non éditable */}
                  <div className="va-param-field full">
                    <label>Secret du webhook post-call <span className="va-param-hint">(variable <code>ELEVENLABS_WEBHOOK_SECRET</code> dans <code>.env</code> — signature <code>ElevenLabs-Signature</code> du webhook post-call)</span></label>
                    <div className="va-param-row">
                      <div className={`va-key-status${webhookSecretConfigured ? ' ok' : ' warn'}`}>
                        {webhookSecretConfigured
                          ? '✅ Configurée dans .env — signature HMAC vérifiée'
                          : '⚠️ Non configurée — le webhook post-call retombe sur X-Api-Key (moins robuste)'}
                      </div>
                    </div>
                    <p className="va-param-desc">
                      Copier le secret HMAC affiché par ElevenLabs à la création du webhook workspace
                      (<em>Paramètres de l'espace de travail</em> → « Webhooks » → webhook post-call).
                      Tant qu'elle est absente, l'ancienne auth <code>X-Api-Key</code> reste active — aucune
                      coupure au moment d'ajouter la clé, mais elle bascule alors immédiatement sur la
                      vérification de signature.
                    </p>
                  </div>

                  {/* RINGOVER_API_KEY */}
                  <div className="va-param-field">
                    <label>Clé API Ringover <span className="va-param-hint">(dashboard Ringover › API)</span></label>
                    <input
                      className="va-param-input"
                      type="password"
                      placeholder="••••••••••••••••"
                      value={globalConfig.ringover_api_key}
                      onChange={e => handleConfigChange('ringover_api_key', e.target.value)}
                    />
                  </div>

                  {/* RINGOVER_IVR_ID */}
                  <div className="va-param-field">
                    <label>ID IVR / Call-flow Ringover <span className="va-param-hint">(dashboard Ringover › IVR)</span></label>
                    <input
                      className="va-param-input"
                      type="text"
                      placeholder="ex. 12345"
                      value={globalConfig.ringover_ivr_id}
                      onChange={e => handleConfigChange('ringover_ivr_id', e.target.value)}
                    />
                  </div>
                </div>

                <div className="va-param-save-row">
                  <button
                    className="btn-primary"
                    onClick={handleSaveConfig}
                    disabled={configSaving}
                  >
                    {configSaving ? 'Enregistrement…' : 'Enregistrer les clés'}
                  </button>
                  {configSaved && <span className="va-save-ok">✅ Enregistré</span>}
                </div>
              </div>

              {/* ── Section 2 : Agent IA par concession (prompt, message d'accueil, voix) ── */}
              <div className="va-param-section">
                <div className="va-param-section-title">🤖 Agent IA commun</div>
                <p className="va-param-desc" style={{ marginBottom: 12 }}>
                  Un seul agent ElevenLabs pour toutes les concessions : même prompt, même voix,
                  mêmes outils. Ce qui change d'une concession à l'autre (nom, horaires,
                  informations) se règle dans la fiche par concession, plus bas. Le prompt est
                  enregistré ici et poussé vers ElevenLabs à chaque sauvegarde.
                </p>

                {!elevenlabsConfigured && (
                  <div className="va-sync-warning">
                    ⚠️ <code>ELEVENLABS_API_KEY</code> non configurée — les prompts seront enregistrés
                    dans le CRM mais ne pourront pas être poussés vers ElevenLabs tant que la clé n'est pas ajoutée au <code>.env</code>.
                  </div>
                )}

                {agentsLoading ? (
                  <div className="va-empty">Chargement…</div>
                ) : agents.length === 0 ? (
                  <div className="va-empty">Aucune concession</div>
                ) : (
                  <div className="va-horaires-list">
                    {/* L'agent commun est rattaché à la concession qui l'a créé ; tant
                        qu'aucun n'existe, chaque concession peut en créer un. */}
                    {(agents.some(a => a.elevenlabs_agent_id) ? agents.filter(a => a.elevenlabs_agent_id) : agents).map(a => (
                      <AgentPromptEditor key={a.concession_id} agent={a} voices={voices} onSave={handleSaveAgent} />
                    ))}
                  </div>
                )}
              </div>

              {/* ── Section 2bis : Annuaire par concession (transfert d'appel) ── */}
              <div className="va-param-section">
                <div className="va-param-section-title">📇 Annuaire par concession</div>
                <p className="va-param-desc" style={{ marginBottom: 12 }}>
                  Collaborateurs que le client peut demander nommément. L'agent ne transfère pas
                  l'appel : il note une demande de rappel à l'attention de la personne trouvée. Le
                  téléphone n'est jamais communiqué à l'agent.
                </p>

                {agentsLoading || annuaireLoading ? (
                  <div className="va-empty">Chargement…</div>
                ) : agents.length === 0 ? (
                  <div className="va-empty">Aucune concession</div>
                ) : (
                  <div className="va-horaires-list">
                    {agents.map(a => (
                      <AnnuaireEditor
                        key={a.concession_id}
                        concessionId={a.concession_id}
                        concessionNom={a.nom}
                        entries={annuaire.filter(e => e.concession_id === a.concession_id)}
                        onAdd={handleAddAnnuaire}
                        onUpdate={handleUpdateAnnuaire}
                        onDelete={handleDeleteAnnuaire}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* ── Section 3 : Endpoints ElevenLabs ── */}
              <div className="va-param-section">
                <div className="va-param-section-title">🔗 Endpoints à configurer dans ElevenLabs</div>
                <p className="va-param-desc" style={{ marginBottom: 12 }}>
                  Dans le dashboard ElevenLabs, configurer chaque outil avec l'URL ci-dessous et le header{' '}
                  <code>X-Api-Key: &lt;VOICE_AGENT_API_KEY&gt;</code>.
                </p>

                <div className="va-endpoints-list">
                  {[
                    { name: 'get_disponibilites', method: 'GET', path: '/api/voice-agent/disponibilites', desc: 'Créneaux libres, groupés par jour — paramètres : concession_slug (requis), duree, date_debut, nb_jours' },
                    { name: 'reserver_rdv',        method: 'POST', path: '/api/voice-agent/rdv',           desc: 'Créer un RDV dans le planning CRM' },
                    { name: 'demander_rappel',     method: 'POST', path: '/api/voice-agent/callback',      desc: 'Enregistrer une demande de rappel (lead)' },
                    { name: 'rechercher_annuaire', method: 'POST', path: '/api/voice-agent/annuaire/recherche', desc: 'Chercher un collaborateur pour transfert d\'appel' },
                  ].map(ep => (
                    <div className="va-endpoint-row" key={ep.name}>
                      <span className={`va-method-badge method-${ep.method.toLowerCase()}`}>{ep.method}</span>
                      <div className="va-endpoint-info">
                        <div className="va-endpoint-name">{ep.name}</div>
                        <div className="va-endpoint-desc">{ep.desc}</div>
                      </div>
                      <code className="va-endpoint-url">{prodBase}{ep.path}</code>
                      <CopyButton value={`${prodBase}${ep.path}`} />
                    </div>
                  ))}

                  <div className="va-endpoint-row webhook">
                    <span className="va-method-badge method-post">POST</span>
                    <div className="va-endpoint-info">
                      <div className="va-endpoint-name">Conversation Initiation Webhook</div>
                      <div className="va-endpoint-desc">
                        Appelé au décrochage — fournit à l'agent la date et l'heure locales, la concession et le n° de l'appelant
                      </div>
                    </div>
                    <code className="va-endpoint-url">{prodBase}/api/voice-agent/init</code>
                    <CopyButton value={`${prodBase}/api/voice-agent/init`} />
                  </div>

                  <div className="va-endpoint-row webhook">
                    <span className="va-method-badge method-post">POST</span>
                    <div className="va-endpoint-info">
                      <div className="va-endpoint-name">Webhook post-call</div>
                      <div className="va-endpoint-desc">
                        Transcript envoyé par ElevenLabs après chaque appel — signé <code>ElevenLabs-Signature</code>{' '}
                        si <code>ELEVENLABS_WEBHOOK_SECRET</code> est renseignée ci-dessus, sinon <code>X-Api-Key</code>
                      </div>
                    </div>
                    <code className="va-endpoint-url">{prodBase}/api/voice-agent/webhook</code>
                    <CopyButton value={`${prodBase}/api/voice-agent/webhook`} />
                  </div>
                </div>

                <p className="va-param-desc" style={{ marginTop: 12 }}>
                  Le transfert d'appel lui-même (<code>transfer_to_number</code>) est un outil natif
                  ElevenLabs, sans URL à configurer — il est activé automatiquement dès qu'un agent est
                  enregistré depuis le CRM.
                </p>
              </div>

              {/* ── Section 3 bis : variables dynamiques du prompt ── */}
              <div className="va-param-section">
                <div className="va-param-section-title">🔤 Variables utilisables dans le prompt</div>
                <p className="va-param-desc" style={{ marginBottom: 12 }}>
                  Renseignées à chaque appel par le <strong>Conversation Initiation Webhook</strong>{' '}
                  ci-dessus. Il se configure à <strong>deux endroits</strong> dans ElevenLabs :
                  <em>Paramètres de l'espace de travail</em> → « Webhook de données client pour
                  l'initiation de conversation », en choisissant le secret <code>X-Api-Key</code>
                  parmi les secrets de l'espace de travail ; puis l'onglet <em>Sécurité</em> de
                  l'agent, le webhook du workspace n'étant qu'un défaut que chaque agent peut
                  remplacer. Sans cela, elles restent vides dans le prompt.
                </p>

                <div className="va-callout-warning">
                  <strong>⚠️ Le widget « Test AI agent » ne reçoit jamais ces variables.</strong>{' '}
                  Le webhook ne se déclenche que sur les <strong>appels entrants téléphoniques</strong>
                  {' '}(Twilio ou SIP trunk), jamais sur les conversations SDK ou WebRTC.
                  Un test lancé depuis le dashboard
                  ElevenLabs affichera donc toujours ces variables à « chaîne vide », même
                  configuration parfaite — et la recherche de créneaux échouera, faute d'identifiant
                  d'établissement. Ce n'est pas une régression :{' '}
                  <strong>l'agent ne se teste valablement que par un vrai appel téléphonique</strong>.
                  Pour garder le widget utilisable malgré tout, renseigner des valeurs par défaut
                  côté ElevenLabs (fonction « Set placeholders »), au minimum pour l'identifiant
                  d'établissement — le webhook les écrase sur les vrais appels.
                </div>

                <div className="va-endpoints-list">
                  {[
                    { name: '{{date_du_jour}}', desc: 'Date du jour à prononcer — ex. « vendredi 7 août 2026 »' },
                    { name: '{{date_iso}}', desc: 'Même date au format des outils — ex. « 2026-08-07 »' },
                    { name: '{{heure_locale}}', desc: 'Heure courante côté concession (UTC−4) — ex. « 13:10 »' },
                    { name: '{{concession_nom}}', desc: 'Nom de la concession appelée — évite de l\'écrire en dur dans le prompt' },
                    { name: '{{concession_slug}}', desc: 'Identifiant à passer aux outils — un même prompt sert alors toutes les concessions' },
                    { name: '{{infos_concession}}', desc: 'Fiche de la concession appelée (horaires, informations) — vide si non renseignée' },
                    { name: '{{caller_id}}', desc: 'Numéro depuis lequel le client appelle — à faire confirmer plutôt qu\'à faire dicter' },
                  ].map(v => (
                    <div className="va-endpoint-row" key={v.name}>
                      <div className="va-endpoint-info">
                        <div className="va-endpoint-name"><code>{v.name}</code></div>
                        <div className="va-endpoint-desc">{v.desc}</div>
                      </div>
                      <CopyButton value={v.name} />
                    </div>
                  ))}
                </div>

                <p className="va-param-desc" style={{ marginTop: 12 }}>
                  ⚠️ Ne jamais utiliser <code>system__time</code> : cette variable n'existe pas côté
                  ElevenLabs et part au modèle sous forme de texte brut, qui invente alors une date.{' '}
                  <code>system__time_utc</code> existe mais renvoie l'heure <strong>UTC</strong> — soit
                  4 heures d'avance sur les Antilles, ce qui décale les rendez-vous d'un jour chaque soir
                  après 20 h. Utiliser <code>{'{{date_du_jour}}'}</code> et <code>{'{{heure_locale}}'}</code>,
                  déjà convertis.
                </p>
              </div>

              {/* ── Section 4 : Fiche par concession ── */}
              <div className="va-param-section">
                <div className="va-param-section-title">🏢 Fiche par concession</div>
                <p className="va-param-desc" style={{ marginBottom: 12 }}>
                  L'agent est commun à toutes les concessions. Au décroché, il reconnaît la concession
                  au numéro du standard appelé, puis lit sa fiche : nom à annoncer, horaires
                  d'ouverture et informations à donner au client. Les demandes et les RDV qu'il crée
                  sont rattachés à cette concession. Cliquer sur une concession pour modifier.
                </p>

                {configLoading ? (
                  <div className="va-empty">Chargement…</div>
                ) : concessions.length === 0 ? (
                  <div className="va-empty">Aucune concession</div>
                ) : (
                  <div className="va-horaires-list">
                    {concessions.map(c => (
                      <FicheConcessionEditor key={c.id} concession={c} onSave={handleSaveFiche} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* ── Modale transcript ─────────────────────────────── */}
      {transcript && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📞 Transcript — {formatDate(transcript.created_at)}</h2>
              <button className="modal-close" onClick={() => setTranscript(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="va-transcript-meta">
                <span><strong>Concession :</strong> {transcript.concession_nom || '—'}</span>
                <span><strong>Durée :</strong> {formatDuration(transcript.duration_seconds)}</span>
                <span><strong>Action :</strong> {ACTION_LABELS[transcript.action_taken]?.label || transcript.action_taken || '—'}</span>
                {transcript.rdv_id && <span><strong>RDV #</strong>{transcript.rdv_id}</span>}
                {transcript.lead_id && <span><strong>Lead #</strong>{transcript.lead_id}</span>}
              </div>
              <pre className="va-transcript-text">{transcript.transcript}</pre>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setTranscript(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VoiceAgent;
